import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { Box, CircularProgress, Typography } from '@mui/material';

import { useModal } from '../../../hooks/useDialog';
import useLocalization from '../../../hooks/useLocalization';
import { mergeSx } from '../../../utils/styles';
import PreMatchWidget from './PreMatchWidget';
import {
  COMPACT_WIDGET_VIEWPORT_QUERY,
  getWidgetColorVariables,
  MOBILE_PORTRAIT_WIDGET_HEIGHT,
  MOBILE_PORTRAIT_WIDGET_VIEWPORT_QUERY,
  PRE_MATCH_WIDGET_MAX_WIDTH,
  PRE_MATCH_WIDGET_MOBILE_LANDSCAPE_WIDTH,
  SHORT_DESKTOP_VIEWPORT_QUERY,
  TABLET_PORTRAIT_WIDGET_HEIGHT,
  TABLET_PORTRAIT_WIDGET_VIEWPORT_QUERY,
  TABLET_PORTRAIT_WIDGET_WIDTH,
  TALL_DESKTOP_VIEWPORT_QUERY,
  TALL_DESKTOP_WIDGET_MAX_WIDTH,
} from './styles';
import type { PreMatchWidgetData } from './types';
import usePreMatchWidgetData from './usePreMatchWidgetData';
import { matchesTeamIdentifier } from './helpers';

export const PRE_MATCH_WIDGET_SWIPE_HANDLE_GUTTER = '24px';

type SeedTeam = {
  id?: string | null;
  name?: string | null;
  shortName?: string | null;
  jersey?: PreMatchWidgetData['fixture']['home']['jersey'];
};

type SeedNamedEntity = {
  id?: string | null;
  name?: string | null;
  shortName?: string | null;
  icon?: string | null;
};

export type PreMatchWidgetSeedEvent = {
  startDate?: string | null;
  competition?: SeedNamedEntity | null;
  tournament?: (SeedNamedEntity & { competition?: SeedNamedEntity | null; sport?: SeedNamedEntity | null }) | null;
  participants?: {
    home?: SeedTeam | null;
    away?: SeedTeam | null;
  } | null;
};

const createSeedWidgetData = (event?: PreMatchWidgetSeedEvent | null): PreMatchWidgetData | null => {
  const home = event?.participants?.home;
  const away = event?.participants?.away;
  const startDate = event?.startDate;

  if (!home?.name || !away?.name || !startDate) return null;

  const competitionName = event.competition?.name ?? event.tournament?.competition?.name ?? '-';
  const tournamentName = event.tournament?.name ?? competitionName;

  return {
    competition: {
      name: competitionName,
    },
    sport: event.tournament?.sport
      ? {
          id: event.tournament.sport.id ?? null,
          name: event.tournament.sport.name ?? event.tournament.sport.shortName ?? null,
          icon: event.tournament.sport.icon ?? null,
        }
      : null,
    tournament: {
      name: tournamentName,
      round: null,
    },
    startDate,
    venue: null,
    fixture: {
      home: {
        name: home.name,
        shortName: home.shortName ?? null,
        participantId: home.id ?? null,
        jersey: home.jersey ?? null,
      },
      away: {
        name: away.name,
        shortName: away.shortName ?? null,
        participantId: away.id ?? null,
        jersey: away.jersey ?? null,
      },
    },
    preview: {
      homeForm: [],
      awayForm: [],
      leaguePositions: {
        home: null,
        away: null,
      },
    },
    standings: {
      league: [],
      home: [],
      away: [],
      legend: [],
    },
    lineups: {
      home: {
        starters: [],
        substitutes: [],
        coach: null,
      },
      away: {
        starters: [],
        substitutes: [],
        coach: null,
      },
      referees: [],
    },
    last5Matches: {
      home: [],
      away: [],
    },
    next5Matches: {
      home: [],
      away: [],
    },
  };
};

const mergeSeedFixtureData = (data: PreMatchWidgetData, seedData: PreMatchWidgetData | null): PreMatchWidgetData => {
  if (!seedData) return data;

  const homeParticipantId = data.fixture.home.participantId ?? seedData.fixture.home.participantId ?? null;
  const awayParticipantId = data.fixture.away.participantId ?? seedData.fixture.away.participantId ?? null;
  const homeFixture = {
    ...data.fixture.home,
    participantId: homeParticipantId,
    jersey: data.fixture.home.jersey ?? seedData.fixture.home.jersey ?? null,
  };
  const awayFixture = {
    ...data.fixture.away,
    participantId: awayParticipantId,
    jersey: data.fixture.away.jersey ?? seedData.fixture.away.jersey ?? null,
  };

  return {
    ...data,
    fixture: {
      home: homeFixture,
      away: awayFixture,
    },
    headToHead: data.headToHead?.map((match) => ({
      ...match,
      homeParticipantId:
        match.homeParticipantId ??
        (matchesTeamIdentifier(homeFixture, match.homeTeam, match.homeTeamShortName)
          ? homeParticipantId
          : matchesTeamIdentifier(awayFixture, match.homeTeam, match.homeTeamShortName)
            ? awayParticipantId
            : null),
      awayParticipantId:
        match.awayParticipantId ??
        (matchesTeamIdentifier(awayFixture, match.awayTeam, match.awayTeamShortName)
          ? awayParticipantId
          : matchesTeamIdentifier(homeFixture, match.awayTeam, match.awayTeamShortName)
            ? homeParticipantId
            : null),
    })),
    sport: data.sport ?? seedData.sport ?? null,
  };
};

export const preMatchWidgetModalStyles = {
  containerColorVariables: getWidgetColorVariables,
  container: {
    width: PRE_MATCH_WIDGET_MAX_WIDTH,
    height: { xs: 'auto', sm: 'auto', md: 'auto' },
    minHeight: { xs: 'auto', sm: 'auto', md: '500px', lg: '600px', xl: '600px' },
    minWidth: 0,
    maxWidth: PRE_MATCH_WIDGET_MAX_WIDTH,
    maxHeight: { xs: '100dvh', md: '90dvh' },
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    p: 0,
    overflow: 'hidden',
    boxSizing: 'border-box',
    [SHORT_DESKTOP_VIEWPORT_QUERY]: {
      height: '100dvh',
      minHeight: 0,
      maxHeight: '100dvh',
    },
    [TALL_DESKTOP_VIEWPORT_QUERY]: {
      width: TALL_DESKTOP_WIDGET_MAX_WIDTH,
      maxWidth: `min(100%, ${TALL_DESKTOP_WIDGET_MAX_WIDTH})`,
    },
    [COMPACT_WIDGET_VIEWPORT_QUERY]: {
      width: PRE_MATCH_WIDGET_MOBILE_LANDSCAPE_WIDTH,
      maxWidth: PRE_MATCH_WIDGET_MOBILE_LANDSCAPE_WIDTH,
      height: '100dvh',
      maxHeight: '100dvh',
    },
    [MOBILE_PORTRAIT_WIDGET_VIEWPORT_QUERY]: {
      width: '100%',
      maxWidth: '100%',
      height: `calc(${MOBILE_PORTRAIT_WIDGET_HEIGHT} + ${PRE_MATCH_WIDGET_SWIPE_HANDLE_GUTTER})`,
      minHeight: 0,
      maxHeight: `calc(${MOBILE_PORTRAIT_WIDGET_HEIGHT} + ${PRE_MATCH_WIDGET_SWIPE_HANDLE_GUTTER})`,
    },
    [TABLET_PORTRAIT_WIDGET_VIEWPORT_QUERY]: {
      width: TABLET_PORTRAIT_WIDGET_WIDTH,
      maxWidth: `min(100%, ${TABLET_PORTRAIT_WIDGET_WIDTH})`,
      height: `calc(${TABLET_PORTRAIT_WIDGET_HEIGHT} + ${PRE_MATCH_WIDGET_SWIPE_HANDLE_GUTTER})`,
      minHeight: 0,
      maxHeight: `calc(${TABLET_PORTRAIT_WIDGET_HEIGHT} + ${PRE_MATCH_WIDGET_SWIPE_HANDLE_GUTTER})`,
    },
  },
  widget: {
    width: '100%',
    maxWidth: '100%',
    height: '100%',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    mx: 'auto',
    minHeight: 0,
  },
  widgetContent: {
    height: '100%',
    maxHeight: '100%',
    [COMPACT_WIDGET_VIEWPORT_QUERY]: {
      height: '100%',
      maxHeight: '100%',
    },
    [MOBILE_PORTRAIT_WIDGET_VIEWPORT_QUERY]: {
      height: MOBILE_PORTRAIT_WIDGET_HEIGHT,
      maxHeight: MOBILE_PORTRAIT_WIDGET_HEIGHT,
    },
    [TABLET_PORTRAIT_WIDGET_VIEWPORT_QUERY]: {
      height: TABLET_PORTRAIT_WIDGET_HEIGHT,
      maxHeight: TABLET_PORTRAIT_WIDGET_HEIGHT,
    },
  },
  widgetWithDesktopCloseButton: {
    '& [data-pmw-close-safe-zone="true"]': {
      pr: { md: 5 },
    },
  },
  desktopCloseButton: {
    position: 'absolute',
    top: 12,
    right: 10,
    zIndex: 4,
    display: { xs: 'none', md: 'inline-flex' },
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
    p: 0,
    borderRadius: '50%',
    border: '1px solid var(--pmw-overlay-border)',
    backgroundColor: 'rgba(255, 255, 255, 0.06)',
    color: 'var(--pmw-muted)',
    cursor: 'pointer',
    backdropFilter: 'blur(6px)',
    transition: 'background-color 120ms ease, color 120ms ease, border-color 120ms ease',
    '&:hover': {
      borderColor: 'var(--pmw-overlay-strong)',
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      color: 'var(--pmw-text)',
    },
  },
  desktopCloseIcon: {
    fontSize: 20,
  },
  swipeHandleGutter: {
    display: { xs: 'flex', md: 'none' },
    alignItems: 'center',
    justifyContent: 'center',
    height: PRE_MATCH_WIDGET_SWIPE_HANDLE_GUTTER,
    flexShrink: 0,
    backgroundColor: 'var(--pmw-shell)',
  },
  swipeHandle: {
    width: 48,
    height: 5,
    borderRadius: 999,
    backgroundColor: 'var(--pmw-overlay-field)',
    pointerEvents: 'none',
  },
  status: {
    minHeight: 320,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    px: 3,
    textAlign: 'center',
  },
  overlay: {
    position: 'absolute',
    inset: 0,
    zIndex: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    px: 3,
    textAlign: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.42)',
    color: 'text.primary',
  },
  unavailableMessage: {
    p: '10px',
    borderRadius: 4,
    backgroundColor: 'background.paper',
  },
};

export type PreMatchWidgetModalProps = {
  statsId?: string | null;
  sportId?: string;
  configurationId: string;
  seedEvent?: PreMatchWidgetSeedEvent | null;
};

const PreMatchWidgetModal = ({ statsId, sportId, configurationId, seedEvent }: PreMatchWidgetModalProps) => {
  const { t } = useLocalization();
  const { closeModal } = useModal();
  const { data, isLoading, isError } = usePreMatchWidgetData(statsId);
  const seedData = createSeedWidgetData(seedEvent);
  const widgetData = data ? mergeSeedFixtureData(data, seedData) : seedData;
  const resolvedWidgetData =
    widgetData && sportId && !widgetData.sport?.id
      ? { ...widgetData, sport: { ...widgetData.sport, id: sportId } }
      : widgetData;
  const unavailableMessage = t('preMatchWidget.modal.unavailable');
  const shouldShowLoadingOverlay = Boolean(widgetData && isLoading);
  const shouldShowUnavailableOverlay = Boolean(widgetData && !isLoading && (isError || !data));

  return (
    <Box
      sx={mergeSx(preMatchWidgetModalStyles.container, preMatchWidgetModalStyles.containerColorVariables)}
      data-testid='pre-match-widget-modal'
      data-stats-id={statsId ?? undefined}
      data-configuration-id={configurationId}
    >
      <Box
        component='button'
        type='button'
        aria-label={t('close')}
        onClick={closeModal}
        sx={preMatchWidgetModalStyles.desktopCloseButton}
      >
        <CloseRoundedIcon sx={preMatchWidgetModalStyles.desktopCloseIcon} />
      </Box>
      <Box sx={preMatchWidgetModalStyles.widget}>
        {!widgetData && isLoading ? (
          <Box sx={preMatchWidgetModalStyles.status}>
            <CircularProgress size={28} />
          </Box>
        ) : !widgetData ? (
          <Box sx={preMatchWidgetModalStyles.status}>
            <Typography variant='body2' sx={preMatchWidgetModalStyles.unavailableMessage}>
              {unavailableMessage}
            </Typography>
          </Box>
        ) : (
          <>
            <PreMatchWidget
              data={resolvedWidgetData ?? widgetData}
              sx={mergeSx(
                preMatchWidgetModalStyles.widgetContent,
                preMatchWidgetModalStyles.widgetWithDesktopCloseButton
              )}
            />
            {shouldShowLoadingOverlay && (
              <Box sx={preMatchWidgetModalStyles.overlay} data-testid='pre-match-widget-loading-overlay'>
                <CircularProgress size={28} />
              </Box>
            )}
            {shouldShowUnavailableOverlay && (
              <Box sx={preMatchWidgetModalStyles.overlay} data-testid='pre-match-widget-unavailable-overlay'>
                <Typography variant='body2' sx={preMatchWidgetModalStyles.unavailableMessage}>
                  {unavailableMessage}
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>
      <Box aria-hidden='true' sx={preMatchWidgetModalStyles.swipeHandleGutter}>
        <Box data-testid='pre-match-widget-swipe-handle' sx={preMatchWidgetModalStyles.swipeHandle} />
      </Box>
    </Box>
  );
};

export default PreMatchWidgetModal;
